import { useState } from 'react';
import { NavLink } from 'react-router-dom';

interface MenuItem {
  icon: string;
  label: string;
  path: string;
  hidden?: boolean;   // 暂时隐藏（保留路由，仅不在菜单显示；恢复=删掉此标记）
}

interface MenuGroup {
  title?: string;
  items: MenuItem[];
}

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);

  // 菜单按真实工作流精简为三组：研究 → 策略 → 系统
  const rawMenuGroups: MenuGroup[] = [
    {
      title: '研究',
      items: [
        { icon: 'ri-database-2-line', label: '因子工作台', path: '/app/factor-workbench' },
        { icon: 'ri-flask-line', label: '批量实验', path: '/app/batch-experiments' },
        // 暂时隐藏（保留路由）
        { icon: 'ri-message-3-line', label: '智研Bot', path: '/app/chat', hidden: true },
        { icon: 'ri-stock-line', label: '个股查询', path: '/app/stock-query', hidden: true },
      ],
    },
    {
      title: '策略',
      items: [
        { icon: 'ri-dashboard-3-line', label: '策略工作台', path: '/app/strategy-workbench' },
        { icon: 'ri-robot-line', label: 'AI推荐', path: '/app/recommendations' },
        { icon: 'ri-wallet-3-line', label: '我的持仓', path: '/app/my-portfolio' },
        { icon: 'ri-article-line', label: '研报研究', path: '/app/analyst' },
        // 暂时隐藏（保留路由）
        { icon: 'ri-file-chart-line', label: '报告中心', path: '/app/report-center', hidden: true },
      ],
    },
    {
      title: '系统',
      items: [
        { icon: 'ri-dashboard-line', label: '监控面板', path: '/app/monitoring' },
        { icon: 'ri-question-line', label: '帮助中心', path: '/app/help' },
      ],
    },
  ];

  // 过滤隐藏项，丢弃变空的分组
  const menuGroups: MenuGroup[] = rawMenuGroups
    .map((g) => ({ ...g, items: g.items.filter((i) => !i.hidden) }))
    .filter((g) => g.items.length > 0);

  return (
    <div
      className={`bg-gradient-to-b from-gray-900 via-gray-900 to-gray-800 text-white flex flex-col shadow-2xl transition-all duration-300 ${
        collapsed ? 'w-[60px]' : 'w-64'
      }`}
    >
      {/* Navigation */}
      <nav className="flex-1 pt-6 pb-4 overflow-y-auto sidebar-scroll">
        <div className={`space-y-3 ${collapsed ? 'px-1.5' : 'px-3'}`}>
          {menuGroups.map((group, groupIndex) => (
            <div key={groupIndex}>
              {/* Group title — hidden when collapsed */}
              {group.title && !collapsed && (
                <div className="px-4 mb-1.5">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    {group.title}
                  </h3>
                </div>
              )}
              {/* Divider between groups when collapsed */}
              {group.title && collapsed && groupIndex > 0 && (
                <div className="mx-2 border-t border-gray-700/50" />
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    data-tour={item.path}
                    title={collapsed ? item.label : undefined}
                    className={({ isActive }) =>
                      `flex items-center rounded-xl transition-all duration-200 cursor-pointer group ${
                        collapsed ? 'justify-center px-1 py-2' : 'space-x-3 px-4 py-2'
                      } ${
                        isActive
                          ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-lg shadow-teal-500/30'
                          : 'text-gray-300 hover:bg-gray-800/50 hover:text-white'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <div
                          className={`flex items-center justify-center rounded-lg transition-all ${
                            collapsed ? 'w-9 h-9' : 'w-10 h-10'
                          } ${
                            isActive ? 'bg-white/20' : 'bg-gray-800/50 group-hover:bg-gray-700/50'
                          }`}
                        >
                          <i className={`${item.icon} text-xl`}></i>
                        </div>
                        {!collapsed && (
                          <span className="text-sm font-medium">{item.label}</span>
                        )}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </div>
      </nav>

      {/* Bottom status — hidden when collapsed */}
      {/* Toggle button */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className={`flex items-center justify-center py-3 border-t border-gray-700/50 text-gray-400 hover:text-white hover:bg-gray-800/50 transition-colors duration-200 ${
          collapsed ? 'px-0' : 'px-4 gap-2'
        }`}
      >
        <i className={`ri-arrow-left-s-line text-lg transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`}></i>
        {!collapsed && <span className="text-xs">收起菜单</span>}
      </button>
    </div>
  );
}
